import { useEffect, useRef, useCallback, useEffect } from 'react';
import Highcharts from 'highcharts';

export function useSynchronizedCharts(themeMode = 'light') {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;

    // Define variables the pinned value box will use
    if (themeMode === 'dark') {
      el.style.setProperty('--hc-vb-text', '#e6e6e6');
      el.style.setProperty('--hc-vb-bg', 'rgba(22,24,30,0.6)');
      el.style.setProperty('--hc-vb-muted', '#9aa0a6');
      el.style.setProperty('--hc-vb-shadow', '0 1px 2px rgba(0,0,0,0.5)');
      // semantic status colors for dark
      el.style.setProperty('--hc-on', '#66bb6a');
      el.style.setProperty('--hc-off', '#ef5350');
      el.style.setProperty('--hc-baseline', '#64b5f6');
      el.style.setProperty('--hc-occupancy', '#ba68c8');
    } else {
      el.style.setProperty('--hc-vb-text', '#111');
      el.style.setProperty('--hc-vb-bg', 'rgba(255,255,255,0.75)');
      el.style.setProperty('--hc-vb-muted', '#666');
      el.style.setProperty('--hc-vb-shadow', '0 1px 2px rgba(0,0,0,0.1)');
      // semantic status colors for light
      el.style.setProperty('--hc-on', '#4caf50');
      el.style.setProperty('--hc-off', '#f44336');
      el.style.setProperty('--hc-baseline', '#2196f3');
      el.style.setProperty('--hc-occupancy', '#7c00c4');
    }
  }, [themeMode]);

  const syncExtremes = useCallback(function (e) {
    const thisChart = this.chart;
    if (e.trigger !== 'syncExtremes') {
      Highcharts.charts.forEach((chart) => {
        if (chart && chart !== thisChart && chart.xAxis[0].setExtremes) {
          chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
            trigger: 'syncExtremes',
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    // Disable default dimming
    Highcharts.Pointer.prototype.reset = function () {
      // Hide tooltip only for the chart that owns this pointer
      if (this.chart) {
        this.chart.tooltip.hide();
        this.chart.xAxis[0].hideCrosshair();
      }
      return undefined; // Don't reset all charts
    };

    // Add events for mouse/touch movement
    ['mousemove', 'touchmove', 'touchstart'].forEach((eventType) => {
      containerRef.current.addEventListener(eventType, (e) => {
        const sourceChart = Highcharts.charts.find((chart) => {
          if (!chart) return false;
          const bounds = chart.container.getBoundingClientRect();
          return (
            e.clientX >= bounds.left &&
            e.clientX <= bounds.right &&
            e.clientY >= bounds.top &&
            e.clientY <= bounds.bottom
          );
        });

        if (!sourceChart) return;

        const sourceEvent = sourceChart.pointer.normalize(e);
        const hoveredXValue = sourceChart.xAxis[0].toValue(sourceEvent.chartX);

        Highcharts.charts.forEach((chart) => {
          if (!chart || !chart.series.length) return;

          let valueHtml = '';

          if (chart.series[0].type === 'xrange') {
            let activationMatch = null;
            let baselineMatch = null;
            let occupancyMatch = null;

            chart.series.forEach((s) => {
              const match = s.data.find(
                (p) => hoveredXValue >= p.x && hoveredXValue <= p.x2,
              );
              if (match) {
                if (match.options.isBaseline) {
                  baselineMatch = match;
                } else if (match.options.isOccupancy) {
                  occupancyMatch = match;
                } else {
                  activationMatch = match;
                }
              }
            });

            const segments = [];

            if (activationMatch) {
              const isOn = !!activationMatch.options.on;
              segments.push(isOn ? 'On' : 'Off');
            }

            if (baselineMatch) {
              segments.push('Baseline Day');
            }

            if (occupancyMatch) {
              const isOccupied = !!occupancyMatch.options.on; // `on` is true if label === true
              segments.push(isOccupied ? 'Occupied' : 'Unoccupied');
            }

            const combinedText = segments.join(' → ');

            const dotColor = activationMatch?.options.on
              ? '#4caf50'
              : baselineMatch
                ? '#2196f3'
                : occupancyMatch
                  ? '#c40000'
                  : '#999';

            valueHtml = `
              <div style="display:flex; align-items:center; justify-content:flex-end; gap:6px;">
                <div style="
                  width:14px;
                  height:14px;
                  border-radius:50%;
                  background-color:${dotColor};
                "></div>
                <span style="font-weight:bold; font-size:16px;">${combinedText}</span>
              </div>
            `;

            const matchToUse =
              activationMatch || baselineMatch || occupancyMatch;
            if (matchToUse) {
              const fakeEvent = {
                chartX: chart.xAxis[0].toPixels(hoveredXValue, true),
                chartY: chart.plotTop + 20,
              };
              if (chart === sourceChart) {
                chart.tooltip.refresh([matchToUse], fakeEvent);
              }
              chart.xAxis[0].drawCrosshair(fakeEvent, matchToUse);
            }
          } else {
            // Line chart logic
            const points = [];
            chart.series.forEach((s) => {
              let closest = null;
              let minDiff = Infinity;
              s.points.forEach((p) => {
                const diff = Math.abs(p.x - hoveredXValue);
                if (diff < minDiff) {
                  minDiff = diff;
                  closest = p;
                }
              });

              if (closest) {
                points.push(closest);

                const category = (s.userOptions.category || '').toLowerCase();
                let iconHtml =
                  '<i class="fa fa-circle-o" style="margin-right:4px;"></i>';
                if (category.includes('heatingpipereturn')) {
                  iconHtml =
                    '<i class="fa-solid fa-temperature-arrow-down"></i>';
                } else if (category.includes('heatingpipesupply')) {
                  iconHtml = '<i class="fa-solid fa-temperature-arrow-up"></i>';
                } else if (category.includes('coldwatersupplypipetemp')) {
                  iconHtml = '<i class="fa-solid fa-temperature-arrow-up"></i>';
                } else if (category.includes('coldwatersupplyairtemp')) {
                  iconHtml = '<i class="fa-solid fa-wind"></i>';
                } else if (category.includes('comfortsensortemp')) {
                  iconHtml = '<i class="fa-solid fa-person-shelter"></i>';
                } else if (category.includes('powerconsumption')) {
                  iconHtml = '<i class="fa-solid fa-person-shelter"></i>';
                } else {
                  iconHtml =
                    '<i class="fa fa-circle-o" style="margin-right:4px;"></i>';
                }
                valueHtml += `
                  <div style="
                    display:flex;
                    align-items:center;
                    justify-content:flex-end;
                    color:${s.color};
                    font-weight:bold;
                    font-size:14px;
                    margin-right: 10px;
                    gap:6px;
                  ">
                    ${iconHtml}
                    <span style="font-size:14px;">${closest.y.toFixed(1)} ${
                      s.userOptions.unitLabel || ''
                    }</span>
                  </div>
                `;
              }
            });

            if (points.length) {
              const fakeEvent = {
                chartX: chart.xAxis[0].toPixels(hoveredXValue, true),
                chartY: chart.plotTop + 20,
              };
              if (chart === sourceChart) {
                // ✅ only show tooltip in hovered chart
                chart.tooltip.refresh(points, fakeEvent);
              }
              chart.xAxis[0].drawCrosshair(fakeEvent, points[0]);
            }
          }

          // Dynamically size and position the box
          if (chart.customValueBox) {
            chart.customValueBox.attr({ text: valueHtml });
            const boxBBox = chart.customValueBox.getBBox();
            const xPos = chart.chartWidth - boxBBox.width - 10;
            chart.customValueBox.attr({ x: xPos, y: 20 });
          }
        });
      });
    });
  }, []);

  return { containerRef, syncExtremes };
}
